import { expandableKeys, makeFilterContext, nextSort } from '@mona-roadmap/domain';
import type { Me, TreeNode, ViewMode } from '@mona-roadmap/domain';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthGate';
import { useExpansion } from '../hooks/useExpansion';
import { useRoadmapModel } from '../hooks/useRoadmapModel';
import { useTheme } from '../hooks/useTheme';
import { useUrlState } from '../hooks/useUrlState';
import { describeError, isApiError } from '../lib/api';
import { columnsFor, filterColumnsFor, type ColumnKey } from '../lib/columns';
import { shortcutFor } from '../lib/filterEditor';
import { describeFilters } from '../lib/filterLabels';
import { isTypingTarget } from '../lib/keyboardNav';
import { createMutations, type RevealRequest } from '../lib/mutations';
import { KEYS, refreshRoadmap, useMetadata, useMondayStatus, useRoadmap } from '../lib/queries';
import { toast } from '../lib/toast';
import { CreateDeliverableDialog, CreateEpicDialog, CreateGoalDialog } from './CreateDialogs';
import { DeliverableDrawer } from './drawers/DeliverableDrawer';
import { EpicDrawer, GoalDrawer } from './drawers/GroupDrawers';
import { FilterEditor, type FilterEditorTarget } from './FilterEditor';
import { Header, type NewKind } from './Header';
import { MondayNotConnected, startMondayConnect } from './MondayStates';
import { RoadmapProvider, type RoadmapContextValue } from './RoadmapContext';
import { TreeGrid } from './TreeGrid';
import { ScreenMessage } from './ui/ScreenMessage';

type Selection = { kind: 'deliverable' | 'goal' | 'epic'; id: string; rowKey: string };
type Creating = { kind: 'deliverable'; prefill: { productGoalId: string | null; epicId: string | null } } | { kind: 'goal' } | { kind: 'epic' };
type Reveal = RevealRequest & { switched?: boolean };

export function RoadmapApp({ me }: { me: Me }) {
  const qc = useQueryClient();
  const auth = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const url = useUrlState();
  const expansion = useExpansion(url.view);
  const roadmap = useRoadmap();
  const metadata = useMetadata();
  const mondayStatus = useMondayStatus(me.role === 'admin');
  const mutations = useMemo(() => createMutations(qc), [qc]);

  const [selection, setSelection] = useState<Selection | null>(null);
  const [creating, setCreating] = useState<Creating | null>(null);
  const [reveal, setReveal] = useState<Reveal | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const lastRowKey = useRef<string | null>(null);

  const snapshot = roadmap.data;
  const meta = metadata.data;
  const columns = useMemo(() => columnsFor(url.view), [url.view]);

  if ((roadmap.error ?? metadata.error) && !(snapshot && meta)) {
    const err = roadmap.error ?? metadata.error;
    if (isApiError(err) && err.code === 'MONDAY_NOT_CONNECTED') return <Shell me={me}><MondayNotConnected isAdmin={me.role === 'admin'} /></Shell>;
    return (
      <Shell me={me}>
        <ScreenMessage
          title="Could not load the roadmap"
          tone="error"
          detail={describeError(err)}
          action={{
            label: 'Retry',
            onClick: () => {
              void roadmap.refetch();
              void metadata.refetch();
            },
          }}
        />
      </Shell>
    );
  }
  if (!snapshot || !meta) return <Shell me={me}><LoadingGrid /></Shell>;

  return (
    <Loaded
      me={me}
      snapshot={snapshot}
      meta={meta}
      cached={snapshot.cached}
      view={url.view}
      setView={url.setView}
      filters={url.filters}
      sort={url.sort}
      setSort={url.setSort}
      patchFilters={url.patchFilters}
      clearFilters={url.clearFilters}
      expansion={expansion}
      columns={columns}
      mutations={mutations}
      theme={theme}
      toggleTheme={toggleTheme}
      mondayStatus={mondayStatus.data}
      connecting={connecting}
      selection={selection}
      setSelection={setSelection}
      creating={creating}
      setCreating={setCreating}
      reveal={reveal}
      setReveal={setReveal}
      refreshing={refreshing}
      fetching={roadmap.isFetching}
      lastRowKey={lastRowKey}
      refetchError={roadmap.isError ? describeError(roadmap.error) : null}
      onRetry={() => void roadmap.refetch()}
      onRefresh={async () => {
        setRefreshing(true);
        try {
          await refreshRoadmap(qc);
          void qc.invalidateQueries({ queryKey: KEYS.metadata });
        } catch (e) {
          toast('error', `Refresh failed: ${describeError(e)}`);
        } finally {
          setRefreshing(false);
        }
      }}
      onConnect={async () => {
        setConnecting(true);
        try {
          await startMondayConnect();
        } catch (e) {
          toast('error', describeError(e));
          setConnecting(false);
        }
      }}
      onSignOut={auth.signOut}
    />
  );
}

type LoadedProps = {
  me: Me;
  snapshot: NonNullable<ReturnType<typeof useRoadmap>['data']>;
  meta: NonNullable<ReturnType<typeof useMetadata>['data']>;
  cached: boolean;
  view: ViewMode;
  setView: (v: ViewMode) => void;
  filters: ReturnType<typeof useUrlState>['filters'];
  sort: ReturnType<typeof useUrlState>['sort'];
  setSort: ReturnType<typeof useUrlState>['setSort'];
  patchFilters: ReturnType<typeof useUrlState>['patchFilters'];
  clearFilters: () => void;
  expansion: ReturnType<typeof useExpansion>;
  columns: ReturnType<typeof columnsFor>;
  mutations: ReturnType<typeof createMutations>;
  theme: ReturnType<typeof useTheme>['theme'];
  toggleTheme: () => void;
  mondayStatus: ReturnType<typeof useMondayStatus>['data'];
  connecting: boolean;
  selection: Selection | null;
  setSelection: (s: Selection | null) => void;
  creating: Creating | null;
  setCreating: (c: Creating | null) => void;
  reveal: Reveal | null;
  setReveal: (r: Reveal | null) => void;
  refreshing: boolean;
  fetching: boolean;
  lastRowKey: React.MutableRefObject<string | null>;
  refetchError: string | null;
  onRetry: () => void;
  onRefresh: () => void;
  onConnect: () => void;
  onSignOut: () => void;
};

function Loaded(p: LoadedProps) {
  const { snapshot, meta, view, filters, expansion, mutations } = p;
  const model = useRoadmapModel(snapshot, meta, filters, view, expansion.overrides, p.sort);
  const filterContext = useMemo(() => makeFilterContext(snapshot.epics, snapshot.productGoals), [snapshot.epics, snapshot.productGoals]);
  const labels = useMemo(
    () => describeFilters(filters, meta.people, { goals: (id) => filterContext.goalsById.get(id)?.name, epics: (id) => filterContext.epicsById.get(id)?.name }),
    [filters, meta.people, filterContext],
  );
  const filterColumns = useMemo(() => filterColumnsFor(view), [view]);

  const { setSelection, setCreating, lastRowKey } = p;
  const openNode = useCallback(
    (node: TreeNode) => {
      if (!node.entityId || (node.kind !== 'deliverable' && node.kind !== 'goal' && node.kind !== 'epic')) return;
      lastRowKey.current = node.key;
      setSelection({ kind: node.kind, id: node.entityId, rowKey: node.key });
    },
    [setSelection, lastRowKey],
  );
  const { patchFilters } = p;
  const isQuickFiltered = useCallback(
    (node: TreeNode) => (node.kind === 'goal' ? filters.goals.length === 1 && filters.goals[0] === node.entityId : node.kind === 'epic' ? filters.epics.length === 1 && filters.epics[0] === node.entityId : false),
    [filters.goals, filters.epics],
  );
  const quickFilter = useCallback(
    (node: TreeNode) => {
      if (!node.entityId) return;
      if (node.kind === 'goal') patchFilters({ goals: isQuickFiltered(node) ? [] : [node.entityId] });
      else if (node.kind === 'epic') patchFilters({ epics: isQuickFiltered(node) ? [] : [node.entityId] });
    },
    [patchFilters, isQuickFiltered],
  );
  const addDeliverableTo = useCallback((node: TreeNode) => setCreating({ kind: 'deliverable', prefill: node.context }), [setCreating]);

  const ctxValue = useMemo<RoadmapContextValue>(
    () => ({
      metadata: meta,
      view,
      filtersActive: model.filtersActive,
      sprintColors: model.sprintColors,
      optionColor: model.optionColor,
      goalsById: model.goalsById,
      epicsById: model.epicsById,
      mutations,
      openNode,
      quickFilter,
      isQuickFiltered,
      addDeliverableTo,
      reveal: p.setReveal,
    }),
    [meta, view, model.filtersActive, model.sprintColors, model.optionColor, model.goalsById, model.epicsById, mutations, openNode, quickFilter, isQuickFiltered, addDeliverableTo, p.setReveal],
  );

  const setExpanded = expansion.setMany;
  const focusTableRef = useRef<() => void>(() => undefined);
  const expandAll = () => {
    expansion.setMany(expandableKeys(model.roots), true);
    focusTableRef.current();
  };
  const collapseAll = () => {
    expansion.setMany(expandableKeys(model.roots), false);
    focusTableRef.current();
  };

  // ---- filter editor + global shortcuts ---------------------------------------------------------------------
  /** Hands keyboard focus back to the table (its current row), so arrows / Enter / Space work right away. */
  const focusTable = useCallback(() => {
    requestAnimationFrame(() => document.querySelector<HTMLElement>('[role="treegrid"] [role="row"][data-key][tabindex="0"]')?.focus({ preventScroll: true }));
  }, []);
  // start in the table
  useEffect(() => {
    focusTable();
  }, [focusTable]);
  focusTableRef.current = focusTable;
  const [editor, setEditor] = useState<FilterEditorTarget | null>(null);
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const openFilter = useCallback((column: ColumnKey | null, anchor: HTMLElement) => setEditor({ column, anchor }), []);

  const latest = useRef({ expandAll, collapseAll, toggleTheme: p.toggleTheme, refresh: p.onRefresh });
  latest.current = { expandAll, collapseAll, toggleTheme: p.toggleTheme, refresh: p.onRefresh };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.repeat) return;
      const action = shortcutFor(e);
      if (!action) return;
      // Never steal keystrokes from inputs, menus, popovers or open drawers/dialogs.
      if (isTypingTarget(e.target) || document.querySelector('[role="dialog"]')) return;
      e.preventDefault();
      if (action === 'expandAll') latest.current.expandAll();
      else if (action === 'collapseAll') latest.current.collapseAll();
      else if (action === 'theme') latest.current.toggleTheme();
      else if (action === 'refresh') latest.current.refresh();
      else if (action === 'search') document.getElementById('roadmap-search')?.focus();
      else if (action === 'filter' && filterButtonRef.current) openFilter(null, filterButtonRef.current);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openFilter]);

  const onNew = (kind: NewKind) =>
    p.setCreating(kind === 'deliverable' ? { kind, prefill: { productGoalId: null, epicId: null } } : { kind });

  // ---- reveal after create ---------------------------------------------------------------------------------
  const revealRef = useRef(p.reveal);
  revealRef.current = p.reveal;
  const { setReveal, setView } = { setReveal: p.setReveal, setView: p.setView };
  const onRevealResult = useCallback(
    (found: boolean) => {
      const r = revealRef.current;
      if (!r) return;
      if (found) {
        setReveal(null);
        return;
      }
      // Epics only exist in the tree once they have deliverables in By Goal; goals only exist By Goal.
      if (!r.switched) {
        if (r.kind === 'epic' && view === 'by-goal') {
          setReveal({ ...r, switched: true });
          setView('by-epic');
          return;
        }
        if (r.kind === 'goal' && view === 'by-epic') {
          setReveal({ ...r, switched: true });
          setView('by-goal');
          return;
        }
      }
      setReveal(null);
      toast('info', 'Created, but it is hidden by the current filters or view.');
    },
    [setReveal, setView, view],
  );

  const closeDrawer = () => p.setSelection(null);
  const refocusRow = () => {
    const key = p.lastRowKey.current;
    if (!key) return;
    requestAnimationFrame(() => document.querySelector<HTMLElement>(`[role="row"][data-key="${CSS.escape(key)}"]`)?.focus({ preventScroll: true }));
  };

  const sel = p.selection;
  const selDeliverable = sel?.kind === 'deliverable' ? snapshot.deliverables.find((d) => d.id === sel.id) : undefined;
  const selGoal = sel?.kind === 'goal' ? snapshot.productGoals.find((g) => g.id === sel.id) : undefined;
  const selEpic = sel?.kind === 'epic' ? snapshot.epics.find((e) => e.id === sel.id) : undefined;
  // The selected item vanished (e.g. a failed optimistic create): close the drawer.
  useEffect(() => {
    if (sel && !selDeliverable && !selGoal && !selEpic) setSelection(null);
  }, [sel, selDeliverable, selGoal, selEpic, setSelection]);

  return (
    <RoadmapProvider value={ctxValue}>
      <div className="flex h-full flex-col">
        <Header
          view={view}
          onViewChange={p.setView}
          filters={filters}
          onSearch={(q) => p.patchFilters({ q })}
          onFocusTable={focusTable}
          filterLabels={labels}
          matched={model.matchedCount}
          total={model.totalCount}
          filterButtonRef={filterButtonRef}
          onOpenFilter={openFilter}
          onPatchFilters={p.patchFilters}
          onClearFilters={p.clearFilters}
          onExpandAll={expandAll}
          onCollapseAll={collapseAll}
          onRefresh={p.onRefresh}
          refreshing={p.refreshing}
          fetching={p.fetching}
          fetchedAt={snapshot.fetchedAt}
          cached={p.cached}
          onNew={onNew}
          me={p.me}
          onSignOut={p.onSignOut}
          theme={p.theme}
          onToggleTheme={p.toggleTheme}
          mondayStatus={p.mondayStatus}
          onConnectMonday={p.onConnect}
          connectingMonday={p.connecting}
        />
        {p.refetchError ? (
          <div role="alert" className="flex items-center gap-2 border-b border-line bg-raised px-3 py-1.5 text-[14px] text-danger">
            Could not refresh from the server: {p.refetchError}
            <button type="button" className="btn !h-8" onClick={p.onRetry}>
              Retry
            </button>
          </div>
        ) : null}
        <TreeGrid
          model={model}
          columns={p.columns}
          filters={filters}
          sort={p.sort}
          onSort={(key) => {
            p.setSort(nextSort(p.sort, key));
            focusTable();
          }}
          onOpenFilter={openFilter}
          onClearFilters={p.clearFilters}
          setExpanded={setExpanded}
          reveal={p.reveal}
          onRevealResult={onRevealResult}
        />
      </div>

      {editor ? (
        <FilterEditor
          key={`${editor.column ?? 'pick'}`}
          target={editor}
          columns={filterColumns}
          filters={filters}
          onChange={p.patchFilters}
          onClose={() => {
            setEditor(null);
            focusTable(); // applying (or cancelling) a filter always lands back in the table
          }}
          deliverables={snapshot.deliverables}
          filterContext={filterContext}
          metadata={meta}
        />
      ) : null}

      {selDeliverable ? <DeliverableDrawer key={selDeliverable.id} deliverable={selDeliverable} goals={snapshot.productGoals} epics={snapshot.epics} onClose={closeDrawer} onCloseFocus={refocusRow} /> : null}
      {selGoal ? (
        <GoalDrawer
          key={selGoal.id}
          goal={selGoal}
          onClose={closeDrawer}
          onCloseFocus={refocusRow}
          onAddDeliverable={() => {
            closeDrawer();
            p.setCreating({ kind: 'deliverable', prefill: { productGoalId: selGoal.id, epicId: null } });
          }}
        />
      ) : null}
      {selEpic ? (
        <EpicDrawer
          key={selEpic.id}
          epic={selEpic}
          onClose={closeDrawer}
          onCloseFocus={refocusRow}
          onAddDeliverable={() => {
            closeDrawer();
            p.setCreating({ kind: 'deliverable', prefill: { productGoalId: null, epicId: selEpic.id } });
          }}
        />
      ) : null}

      {p.creating?.kind === 'deliverable' ? (
        <CreateDeliverableDialog
          prefill={p.creating.prefill}
          goals={snapshot.productGoals}
          epics={snapshot.epics}
          metadata={meta}
          sprintColors={model.sprintColors}
          onClose={() => p.setCreating(null)}
          onSubmit={(input) => {
            p.setCreating(null);
            void mutations.createDeliverable(input, { onInserted: setReveal, onCreated: setReveal });
          }}
        />
      ) : null}
      {p.creating?.kind === 'goal' ? (
        <CreateGoalDialog
          metadata={meta}
          onClose={() => p.setCreating(null)}
          onSubmit={(input) => {
            p.setCreating(null);
            void mutations.createGoal(input, { onInserted: setReveal, onCreated: setReveal });
          }}
        />
      ) : null}
      {p.creating?.kind === 'epic' ? (
        <CreateEpicDialog
          metadata={meta}
          onClose={() => p.setCreating(null)}
          onSubmit={(input) => {
            p.setCreating(null);
            void mutations.createEpic(input, { onInserted: setReveal, onCreated: setReveal });
          }}
        />
      ) : null}
    </RoadmapProvider>
  );
}

/** Minimal chrome shown while loading / on fatal states (header content needs data). */
function Shell({ me, children }: { me: Me; children: React.ReactNode }) {
  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center gap-2 border-b border-line bg-panel px-3 py-2">
        <span className="grid size-6 place-items-center rounded-md bg-accent text-[15px] font-bold text-white" aria-hidden>
          M
        </span>
        <h1 className="text-[16px] font-semibold tracking-tight">Mona Roadmap</h1>
        <span className="ml-auto text-[13.5px] text-faint">{me.email}</span>
      </header>
      <div className="min-h-0 flex-1">{children}</div>
    </div>
  );
}

function LoadingGrid() {
  return (
    <div className="space-y-px p-3" aria-busy="true" aria-label="Loading roadmap" role="status">
      {Array.from({ length: 14 }, (_, i) => (
        <div key={i} className="skeleton h-[30px]" style={{ width: `${100 - (i % 4) * 6}%`, opacity: 1 - i * 0.05 }} />
      ))}
    </div>
  );
}
